// ─── Report Generation Button ──────────────────────────────────────────────────
//
// Button to generate a PDF patient report. Fetches all needed data and
// calls the report generator. Placed on Dashboard (own data) and
// PatientDetail (clinician view).
// ──────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { supabase, getCurrentUser } from '../lib/supabase';
import { APPLE_HEALTH_METRIC_NAMES, classifyMetric } from '../lib/metricNames';
import { PhenotypeInput } from '../lib/phenotype';
import type { ReportData } from '../lib/reportGenerator';
import { DailyMetrics, PHQ4Entry } from '../types';
import { useDemo } from '../lib/demoContext';
import {
  DEMO_PROFILE, DEMO_METRICS, DEMO_PHQ4_ENTRIES,
  DEMO_KEY_LABS, DEMO_PHENOTYPE_INPUT, DEMO_APPLE_EXTRAS,
  DEMO_CHECKIN,
} from '../lib/demoData';

// Dynamic import — pulls in reportGenerator.ts (~2200 lines) + jspdf
// (~150 KB) only when the user clicks Export PDF. Keeps those out of
// the initial dashboard chunk.
async function loadReportGenerator() {
  const mod = await import('../lib/reportGenerator');
  return mod.generatePatientReport;
}

interface ReportButtonProps {
  // For clinician view — provide patient data directly
  patientId?: string;
  patientName?: string;
  patientDOB?: string;
  // Pre-loaded data (optional — if not provided, will fetch)
  preloadedMetrics?: DailyMetrics[];
  preloadedPhq4?: PHQ4Entry[];
  // Compact mode
  compact?: boolean;
}

export default function ReportButton({
  patientId,
  patientName,
  patientDOB,
  preloadedMetrics,
  preloadedPhq4,
  compact = false,
}: ReportButtonProps) {
  const demo = useDemo();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      // Demo mode — use pre-built data, skip Supabase
      if (demo) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const labs: ReportData['labs'] = DEMO_KEY_LABS.map(l => ({
          marker: l.marker, value: l.value, unit: l.unit,
          ref_low: l.ref_low, ref_high: l.ref_high, date: l.date,
        }));
        const generatePatientReport = await loadReportGenerator();
        generatePatientReport({
          patientName: DEMO_PROFILE.full_name,
          patientDOB: DEMO_PROFILE.dob as string | undefined,
          patientId: DEMO_PROFILE.id,
          startDate: fmt(startDate),
          endDate: fmt(endDate),
          metrics: DEMO_METRICS,
          phq4Entries: DEMO_PHQ4_ENTRIES,
          labs,
          phenotypeInput: DEMO_PHENOTYPE_INPUT,
          checkin: {
            diet: DEMO_CHECKIN.diet ?? undefined,
            stress: DEMO_CHECKIN.stress ?? undefined,
            substances: DEMO_CHECKIN.substances ?? undefined,
            social: DEMO_CHECKIN.social ?? undefined,
            appetite: DEMO_CHECKIN.appetite ?? undefined,
          },
          appleExtras: {
            restingHR: DEMO_APPLE_EXTRAS.restingHR,
            weightDelta: DEMO_APPLE_EXTRAS.weightDelta,
            bmi: DEMO_APPLE_EXTRAS.bmi,
            bodyFat: DEMO_APPLE_EXTRAS.bodyFat,
            vo2Max: DEMO_APPLE_EXTRAS.vo2Max,
          },
        });
        setGenerating(false);
        return;
      }

      // Determine user ID
      let userId = patientId;
      let name = patientName || 'Patient';
      let dob = patientDOB;

      if (!userId) {
        const user = await getCurrentUser();
        if (!user) { setGenerating(false); return; }
        userId = user.id;
      }

      // Fetch profile if name not provided
      if (!patientName || !dob) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, dob, oura_token')
          .eq('id', userId)
          .single();
        if (profile) {
          if (!patientName) name = profile.full_name || 'Patient';
          if (!dob) dob = profile.dob || undefined;
        }
      }

      // Date range: last 30 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      // Fetch metrics from daily_health_summary (synced Oura + Apple Health)
      let metrics: DailyMetrics[] = preloadedMetrics || [];
      if (!preloadedMetrics || preloadedMetrics.length === 0) {
        const { data: summaryRows } = await supabase
          .from('daily_health_summary')
          .select('*')
          .eq('user_id', userId)
          .gte('date', startStr)
          .lte('date', endStr)
          .order('date', { ascending: true });

        if (summaryRows && summaryRows.length > 0) {
          metrics = summaryRows.map(r => ({
            date: r.date,
            sleep_score: r.sleep_score,
            hrv: r.hrv_rmssd ?? r.session_hrv,
            readiness: r.readiness_score,
            steps: r.steps,
            exercise_mins: r.high_activity_mins != null ? Math.round(r.high_activity_mins) : null,
            stress_score: r.daytime_stress,
            total_sleep_mins: r.total_sleep_mins,
            sleep_efficiency: r.sleep_efficiency,
            deep_sleep_mins: r.deep_sleep_mins,
            lowest_hr: r.lowest_heart_rate,
            rem_sleep_mins: null, light_sleep_mins: null, bedtime_start: null,
          }));
        }

        // Merge Apple Health steps/exercise for dates that have it
        const { data: appleRows } = await supabase
          .from('health_metrics')
          .select('metric_name, date, value')
          .eq('user_id', userId)
          .eq('source', 'apple_health')
          .gte('date', startStr)
          .in('metric_name', [...APPLE_HEALTH_METRIC_NAMES]);

        if (appleRows && appleRows.length > 0) {
          const stepsMap: Record<string, number> = {};
          const exerciseMap: Record<string, number> = {};
          appleRows.forEach(row => {
            const category = classifyMetric(row.metric_name);
            if (category === 'steps') stepsMap[row.date] = (stepsMap[row.date] || 0) + row.value;
            else if (category === 'exercise' || category === 'active_energy') exerciseMap[row.date] = (exerciseMap[row.date] || 0) + row.value;
          });

          metrics = metrics.map(m => ({
            ...m,
            steps: stepsMap[m.date] != null ? Math.round(stepsMap[m.date]) : m.steps,
            exercise_mins: exerciseMap[m.date] != null ? Math.round(exerciseMap[m.date]) : m.exercise_mins,
          }));
        }
      }

      // Fetch PHQ-4
      let phq4: PHQ4Entry[] = preloadedPhq4 || [];
      if (!preloadedPhq4 || preloadedPhq4.length === 0) {
        const { data: phq4Rows } = await supabase
          .from('phq4')
          .select('*')
          .eq('user_id', userId)
          .order('week', { ascending: false })
          .limit(8);
        if (phq4Rows) phq4 = phq4Rows as PHQ4Entry[];
      }

      // Fetch labs
      const { data: labRows } = await supabase
        .from('labs')
        .select('marker, value, unit, ref_low, ref_high, date')
        .eq('user_id', userId)
        .order('date', { ascending: false });

      // Deduplicate labs (keep most recent per marker)
      const labs: ReportData['labs'] = [];
      const seenMarkers = new Set<string>();
      if (labRows) {
        for (const row of labRows) {
          if (!seenMarkers.has(row.marker)) {
            seenMarkers.add(row.marker);
            labs.push(row);
          }
        }
      }

      // Build phenotype input
      let phenotypeInput: PhenotypeInput | undefined;
      if (phq4.length > 0) {
        const p = phq4[0];
        phenotypeInput = { phq4: { q1: p.q1, q2: p.q2, q3: p.q3, q4: p.q4 } };

        // Try to get weekly check-in for pillars/symptoms
        const { data: checkinRows } = await supabase
          .from('weekly_checkin')
          .select('*')
          .eq('user_id', userId)
          .order('week', { ascending: false })
          .limit(1);

        if (checkinRows && checkinRows.length > 0) {
          const c = checkinRows[0];
          phenotypeInput.pillars = {
            diet: c.diet ?? -1,
            stress: c.stress ?? -1,
            substances: c.substances ?? -1,
            social: c.social ?? -1,
          };
          if (c.symptoms) {
            phenotypeInput.symptoms = c.symptoms.split(',').map((s: string) => s.trim()).filter(Boolean);
          }
          if (c.appetite != null) {
            phenotypeInput.appetite = c.appetite;
          }
        }

        // Labs for phenotype
        if (labs.length > 0) {
          const labMap: Record<string, number> = {};
          const seen = new Set<string>();
          for (const row of labs) {
            const key = row.marker.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (!seen.has(key)) {
              seen.add(key);
              labMap[key] = row.value;
            }
          }
          phenotypeInput.labs = {
            hsCRP: labMap['hscrp'] ?? labMap['crp'],
            insulin: labMap['insulin'],
            glucose: labMap['glucose'],
            hba1c: labMap['hba1c'],
            cortisol: labMap['cortisol'],
            testosterone: labMap['testosterone'],
            vitaminD: labMap['vitamind'],
            tsh: labMap['tsh'],
            // IMHC Tier 1 — foundational rule-outs
            freeT4: labMap['freet4'],
            b12: labMap['b12'],
            folate: labMap['folate'],
            ferritin: labMap['ferritin'],
            // IMHC Tier 2 — phenotype signals
            triglycerides: labMap['triglycerides'],
            omega3Index: labMap['omega3index'],
          };
        }
      }

      // Build checkin data for pillar scoring in report
      let checkinForReport: ReportData['checkin'] | undefined;
      if (phenotypeInput?.pillars) {
        checkinForReport = {
          diet: phenotypeInput.pillars.diet >= 0 ? phenotypeInput.pillars.diet : undefined,
          stress: phenotypeInput.pillars.stress >= 0 ? phenotypeInput.pillars.stress : undefined,
          substances: phenotypeInput.pillars.substances >= 0 ? phenotypeInput.pillars.substances : undefined,
          social: phenotypeInput.pillars.social >= 0 ? phenotypeInput.pillars.social : undefined,
          appetite: phenotypeInput.appetite,
        };
      }

      // Fetch pre-computed pillar scores (single source of truth)
      let pillarScores: ReportData['pillarScores'] | undefined;
      let pillarDeltas: Record<string, number> | undefined;
      const { data: psRows } = await supabase
        .from('pillar_scores')
        .select('*')
        .eq('user_id', userId)
        .order('data_date', { ascending: false })
        .limit(2);
      if (psRows && psRows.length > 0) {
        pillarScores = psRows[0];
        // Compute deltas from prior period if two rows available
        if (psRows.length >= 2) {
          const cur = psRows[0];
          const prev = psRows[1];
          pillarDeltas = {};
          for (const key of ['sleep', 'movement', 'nutrition', 'stress', 'social', 'substance'] as const) {
            if (cur[key] != null && prev[key] != null) {
              pillarDeltas[key] = (cur[key] as number) - (prev[key] as number);
            }
          }
        }
      }

      // Generate PDF
      const generatePatientReport = await loadReportGenerator();
      generatePatientReport({
        patientName: name,
        patientDOB: dob,
        patientId: userId,
        startDate: startStr,
        endDate: endStr,
        metrics,
        phq4Entries: phq4,
        labs,
        phenotypeInput,
        checkin: checkinForReport,
        pillarScores,
        pillarDeltas,
      });

    } catch (e: unknown) {
      console.error('[ReportButton] Error generating report:', e);
      setError(e instanceof Error ? e.message : 'Failed to generate report. Please try again.');
    }
    setGenerating(false);
  }

  if (compact) {
    return (
      <div>
      {error && <p className="text-xs text-red-500 mb-1">{error}</p>}
      <button
        onClick={handleGenerate}
        disabled={generating}
        className="text-xs font-medium flex items-center gap-1 disabled:opacity-50"
        style={{ color: '#6F8A5A', transition: 'color 0.3s ease' }}
        onMouseEnter={e => { if (!generating) e.currentTarget.style.color = '#2F4A34'; }}
        onMouseLeave={e => { e.currentTarget.style.color = '#6F8A5A'; }}
      >
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        {generating ? 'Generating...' : 'Export PDF'}
      </button>
      </div>
    );
  }

  return (
    <div>
      {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
      <button
        onClick={handleGenerate}
        disabled={generating}
        className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        {generating ? 'Generating Report...' : 'Export Patient Report'}
      </button>
    </div>
  );
}
